/**
 * Earn & Invite (OutlierOS /earn replica, docs/legacy-ui/03 §12): header → invite link → 4 stats → referrals and
 * free-month rewards → affiliate section (payout form, balances, commission history) or the revenue-share CTA → footer.
 * Changed (DASHBOARD_PARITY §3.2): no trial for invited friends (decision 14); the reward is N months of the plan
 * the friend bought (decision 15); affiliates belong to a program (decision 16) and balances are per currency.
 */
import { date as fmtDate } from '../format';
import { useEffect, useMemo, useState } from 'react';
import { Check, Copy, Gift, HandCoins, Loader2, Users, Wallet } from 'lucide-react';
import { ALL_COUNTRIES, PAYOUT_METHOD_LABELS, PRIORITY_COUNTRIES, methodsForCountry, payoutFields, type PayoutMethod } from '@vw/core/src/payouts';
import { BRAND } from '@vw/core/src/brand';
import { Button, ErrorState, Input, Pill, Select, Skeleton, cn } from '@vw/ui';
import { api, ApiError } from '../api';

interface Balance { currency: string; pending: number; approved: number; paid: number; lifetime: number }
interface Reward { months: number; plan: string; status: string; method: string | null; note: string | null; createdAt: number; appliedAt: number | null }
interface Commission { created_at: number; currency: string; amount_cents: number; commission_bps: number; commission_cents: number; status: string; available_at: number; paid_at: number | null; reference: string | null }
interface Payout { summary: string | null; updatedAt: number | null }
interface PayoutFull { country: string; method: PayoutMethod; holder: string; details: Record<string, string> }
interface EarnInfo {
  code: string; link: string;
  settings: { rewardMonths: number; attributionDays: number };
  stats: { signups: number; customers: number };
  referrals: Array<{ email: string; joinedAt: number; paid: boolean }>;
  rewards: Reward[];
  affiliate: null | { status: 'active' | 'paused'; program: string; commissionPct: number; durationMonths: number | null; holdDays: number; attributionDays: number; minPayout: Record<string, number>; payout: Payout | null; payoutsEnabled: boolean };
  balances: Balance[];
  commissions: Commission[];
}

const money = (cents: number, currency: string) => (cents / 100).toLocaleString(currency === 'INR' ? 'en-IN' : 'en-US', { style: 'currency', currency, maximumFractionDigits: cents % 100 ? 2 : 0 });
const sumOf = (b: Balance[], k: keyof Omit<Balance, 'currency'>) => (b.length ? b.map((x) => money(x[k], x.currency)).join(' + ') : money(0, 'USD'));
const date = (ms: number | null) => (ms ? fmtDate(ms) : '—');
const TONE: Record<string, 'amber' | 'sky' | 'emerald' | 'muted' | 'rose'> = { pending: 'amber', approved: 'sky', paid: 'emerald', applied: 'emerald', active: 'emerald', void: 'muted', paused: 'muted', failed: 'amber', applying: 'amber' };

function Stat({ icon: Icon, label, value }: { icon: typeof Gift; label: string; value: string | number }) {
  return (
    <div className="rounded-2xl border border-border bg-card p-4 shadow-xs">
      <div className="flex items-center gap-2 text-xs font-semibold text-muted-foreground"><Icon className="h-4 w-4" />{label}</div>
      <div className="mt-1 text-xl font-black tabular-nums">{value}</div>
    </div>
  );
}

export function Earn() {
  const [d, setD] = useState<EarnInfo | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const load = () => api<EarnInfo>('/api/referrals/me').then((x) => { setD(x); setError(null); }, (e) => setError(e.message));
  useEffect(() => { void load(); }, []);

  if (error) return <ErrorState message={`Could not load: ${error}`} onRetry={load} />;
  if (!d) return <div className="mx-auto max-w-5xl space-y-4"><Skeleton className="h-24" /><Skeleton className="h-32" /><Skeleton className="h-64" /></div>;

  const aff = d.affiliate;
  const active = aff?.status === 'active';
  const months = d.settings.rewardMonths;
  async function copy() {
    try {
      await navigator.clipboard.writeText(d!.link);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      window.prompt('Copy your invite link', d!.link);
    }
  }
  const monthsOf = (statuses: string[]) => d.rewards.filter((r) => statuses.includes(r.status)).reduce((n, r) => n + r.months, 0);
  const openRewards = monthsOf(['pending', 'applying', 'failed']);
  const appliedRewards = monthsOf(['applied']);

  return (
    <div className="mx-auto max-w-5xl space-y-6">
      <div>
        <h1 className="text-3xl font-extrabold tracking-tight">Earn & Invite</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Invite friends to {BRAND.name}.{' '}
          {active ? <>You earn <b>{aff!.commissionPct}%</b> of every payment they make{aff!.durationMonths ? ` for their first ${aff!.durationMonths} months` : ''}.</>
            : months > 0 ? <>You get <b>{months} month{months === 1 ? '' : 's'} free</b> of the plan they buy when they subscribe to a paid plan (free sign-ups don't count).</>
            : <>Referral rewards are paused right now.</>}
        </p>
      </div>

      <section className="rounded-2xl border border-border bg-card p-5 shadow-xs">
        <h2 className="flex items-center gap-2 font-extrabold"><Gift className="h-4 w-4 text-primary" />Your invite link</h2>
        <div className="mt-3 flex flex-col gap-2 sm:flex-row">
          <Input readOnly value={d.link} onFocus={(e) => e.currentTarget.select()} className="font-mono text-xs" aria-label="Your invite link" />
          <Button onClick={copy} className="shrink-0">{copied ? <><Check className="h-4 w-4" />Copied</> : <><Copy className="h-4 w-4" />Copy link</>}</Button>
        </div>
        <p className="mt-2 text-xs text-muted-foreground">Code <b className="font-mono">{d.code}</b> · works for new accounts that sign up within {active ? aff!.attributionDays : d.settings.attributionDays} days of clicking.</p>
      </section>

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Stat icon={Users} label="Friends signed up" value={d.stats.signups} />
        <Stat icon={Check} label="Became customers" value={d.stats.customers} />
        {active ? <>
          <Stat icon={HandCoins} label="Earned (lifetime)" value={sumOf(d.balances, 'lifetime')} />
          <Stat icon={Wallet} label="Ready for payout" value={sumOf(d.balances, 'approved')} />
        </> : <>
          <Stat icon={Gift} label="Free months pending" value={openRewards} />
          <Stat icon={Gift} label="Free months applied" value={appliedRewards} />
        </>}
      </div>

      <div className={cn('grid gap-4', !active && 'lg:grid-cols-2')}>
        <section className="rounded-2xl border border-border bg-card p-5 shadow-xs">
          <h2 className="font-extrabold">Your referrals</h2>
          {d.referrals.length === 0 ? <p className="mt-3 text-sm text-muted-foreground">No one has joined with your link yet. Share it with a creator friend who researches Reels.</p> : (
            <ul className="mt-3 divide-y divide-border text-sm">
              {d.referrals.map((r, i) => (
                <li key={i} className="flex items-center justify-between py-2"><span className="font-mono text-xs">{r.email}</span>
                  <span className="flex items-center gap-2 text-xs text-muted-foreground">{date(r.joinedAt)}<Pill tone={r.paid ? 'emerald' : 'amber'}>{r.paid ? 'paid' : 'pending'}</Pill></span></li>
              ))}
            </ul>
          )}
        </section>
        {!active && (
          <section className="rounded-2xl border border-border bg-card p-5 shadow-xs">
            <h2 className="font-extrabold">Free-month rewards</h2>
            {d.rewards.length === 0 ? <p className="mt-3 text-sm text-muted-foreground">{months > 0 ? <>When a friend you invited first pays for a plan, you get {months} month{months === 1 ? '' : 's'} of that plan free: if you pay, your next payment moves out; otherwise the plan switches on (after your current access ends, if you're cancelling). Rewards add up.</> : 'Referral rewards are paused right now.'}</p> : (
              <ul className="mt-3 divide-y divide-border text-sm">
                {d.rewards.map((r, i) => {
                  const shown = r.status === 'applying' || r.status === 'failed' ? 'pending' : r.status;
                  return (
                    <li key={i} className="py-2">
                      <div className="flex items-center justify-between"><b>{r.months} month{r.months === 1 ? '' : 's'} free · {r.plan}</b>
                        <span className="flex items-center gap-2 text-xs text-muted-foreground">{date(r.createdAt)}<Pill tone={TONE[shown] ?? 'muted'}>{shown}</Pill></span></div>
                      <p className="mt-0.5 text-xs text-muted-foreground">{r.status === 'applied' ? (r.method === 'billing_date' ? `Applied: ${r.note ?? 'your next billing date moved'}.` : r.method === 'grant' ? `Applied: ${r.note ?? 'plan active'}.` : 'Applied.')
                        : r.status === 'void' ? 'Not applicable (the payment was refunded).' : 'Being applied to your account.'}</p>
                    </li>
                  );
                })}
              </ul>
            )}
          </section>
        )}
      </div>

      {aff ? (
        <section className="rounded-2xl border border-border bg-card p-5 shadow-xs">
          <h2 className="flex items-center gap-2 font-extrabold"><HandCoins className="h-4 w-4 text-primary" />Affiliate program (revenue share)<Pill tone={TONE[aff.status] ?? 'muted'}>{aff.status}</Pill><span className="text-xs font-semibold text-muted-foreground">{aff.program}</span></h2>
          {!active && <p className="mt-2 text-sm text-muted-foreground">Your revenue share is paused, so your link earns free months for now. Your balances are kept. Questions? Email <a className="text-primary underline" href={`mailto:${BRAND.supportEmail}`}>{BRAND.supportEmail}</a>.</p>}
          <div className={cn('mt-4 grid gap-6', active && 'lg:grid-cols-2')}>
            <div>
              <h3 className="text-sm font-bold">Payout details</h3>
              <PayoutForm initial={aff.payout} enabled={aff.payoutsEnabled} onSaved={load} />
            </div>
            {active && (
              <div className="grid h-fit grid-cols-2 gap-3">
                <Stat icon={Wallet} label="In refund window" value={sumOf(d.balances, 'pending')} />
                <Stat icon={Wallet} label="Ready for payout" value={sumOf(d.balances, 'approved')} />
                <Stat icon={HandCoins} label="Paid to you" value={sumOf(d.balances, 'paid')} />
                <Stat icon={HandCoins} label="Lifetime" value={sumOf(d.balances, 'lifetime')} />
                <p className="col-span-2 text-xs text-muted-foreground">Commissions are payable {aff.holdDays} days after each payment (the refund window). Minimum payout: {Object.entries(aff.minPayout).filter(([, v]) => v > 0).map(([c, v]) => money(v, c)).join(' or ') || 'none'}.</p>
              </div>
            )}
          </div>
          {active && (
            <div className="mt-6">
              <h3 className="text-sm font-bold">Commission history</h3>
              {d.commissions.length === 0 ? <p className="mt-2 text-sm text-muted-foreground">No commissions yet. They appear here when a customer you referred pays.</p> : (
                <div className="mt-2 overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="text-left text-xs text-muted-foreground"><tr><th className="py-2">Date</th><th>Payment</th><th>Rate</th><th>Commission</th><th>Status</th><th>Payable from / ref</th></tr></thead>
                    <tbody className="divide-y divide-border">
                      {d.commissions.map((c, i) => (
                        <tr key={i}><td className="py-2">{date(c.created_at)}</td><td>{money(c.amount_cents, c.currency)}</td><td>{c.commission_bps / 100}%</td><td className="font-semibold">{money(c.commission_cents, c.currency)}</td>
                          <td><Pill tone={TONE[c.status] ?? 'muted'}>{c.status}</Pill></td><td className="text-xs text-muted-foreground">{c.status === 'paid' ? c.reference ?? date(c.paid_at) : date(c.available_at)}</td></tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </section>
      ) : (
        <section className="rounded-2xl border border-primary/20 bg-primary/5 p-5">
          <h2 className="flex items-center gap-2 font-extrabold"><HandCoins className="h-4 w-4 text-primary" />Have an audience? Ask about revenue share</h2>
          <p className="mt-1 text-sm text-muted-foreground">Creators, educators and agencies can earn a share of every payment from the people they bring.</p>
          <a className="mt-3 inline-flex rounded-xl bg-primary px-4 py-2 text-sm font-bold text-primary-foreground" href={`mailto:${BRAND.supportEmail}?subject=${encodeURIComponent(`${BRAND.name} revenue share`)}`}>Email us</a>
        </section>
      )}

      <p className="text-xs text-muted-foreground">Self-referrals, duplicate accounts and refunded payments don't earn rewards. Program terms may change; earned balances are always honoured.</p>
    </div>
  );
}

function PayoutForm({ initial, enabled, onSaved }: { initial: Payout | null; enabled: boolean; onSaved: () => void }) {
  const [editing, setEditing] = useState(!initial?.summary);
  const [loading, setLoading] = useState(false);
  const [country, setCountry] = useState('IN');
  const [method, setMethod] = useState<PayoutMethod>('upi');
  const [holder, setHolder] = useState('');
  const [details, setDetails] = useState<Record<string, string>>({});
  // The full details are only fetched when you choose to edit them (lists and page loads show the masked summary).
  async function edit() {
    setLoading(true);
    try {
      const { payout } = await api<{ payout: PayoutFull | null }>('/api/affiliate/payout');
      if (payout) { setCountry(payout.country); setMethod(payout.method); setHolder(payout.holder); setDetails(payout.details); }
      setEditing(true);
    } catch (e) { setState((e as Error).message); } finally { setLoading(false); }
  }
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [state, setState] = useState<'idle' | 'saving' | 'saved' | string>('idle');
  const [names, setNames] = useState<Intl.DisplayNames | null>(null);
  useEffect(() => { try { setNames(new Intl.DisplayNames(['en'], { type: 'region' })); } catch { setNames(null); } }, []);
  const methods = methodsForCountry(country);
  useEffect(() => { if (!methods.includes(method)) setMethod(methods[0]!); }, [country]);
  const fields = payoutFields(methods.includes(method) ? method : methods[0]!, country);
  const others = useMemo(() => ALL_COUNTRIES.filter((c) => !PRIORITY_COUNTRIES.includes(c)).sort((a, b) => (names?.of(a) ?? a).localeCompare(names?.of(b) ?? b)), [names]);
  const label = (c: string) => names?.of(c) ?? c;

  async function save(e: React.FormEvent) {
    e.preventDefault();
    setState('saving');
    setErrors({});
    try {
      await api('/api/affiliate/payout', { method: 'PUT', json: { country, method, holder, details: Object.fromEntries(fields.map((f) => [f.key, details[f.key] ?? ''])) } });
      setState('saved');
      setEditing(false);
      onSaved();
    } catch (err) {
      if (err instanceof ApiError && err.body?.errors) setErrors(err.body.errors as Record<string, string>);
      setState((err as Error).message);
    }
  }
  if (!enabled) return <p className="mt-2 text-sm text-muted-foreground">{initial?.summary ? <>Saved: <b>{initial.summary}</b>. </> : ''}Payout details can't be edited here yet. Email <a className="text-primary underline" href={`mailto:${BRAND.supportEmail}`}>{BRAND.supportEmail}</a>.</p>;
  const err = (k: string) => (errors[k] ? <p id={`payout-err-${k}`} className="mt-1 text-xs text-rose-600 dark:text-rose-400">{errors[k]}</p> : null);
  const described = (k: string) => (errors[k] ? { 'aria-invalid': true, 'aria-describedby': `payout-err-${k}` } : {});
  if (!editing) return (
    <div className="mt-2 space-y-2 text-sm">
      <p>Saved: <b>{initial?.summary}</b>{initial?.updatedAt ? <span className="text-xs text-muted-foreground"> · updated {date(initial.updatedAt)}</span> : null}</p>
      <Button variant="secondary" size="sm" onClick={edit} loading={loading}>Edit payout details</Button>
      {state === 'saved' && <span className="ml-2 text-xs font-semibold text-emerald-600">Saved.</span>}
    </div>
  );
  return (
    <form onSubmit={save} className="mt-2 space-y-3 text-sm">
      <label className="block"><span className="text-xs font-semibold">Country</span>
        <Select value={country} onChange={(e) => setCountry(e.target.value)} className="mt-1 w-full" {...described('country')}>
          {PRIORITY_COUNTRIES.map((c) => <option key={c} value={c}>{label(c)}</option>)}
          <option disabled>──────────</option>
          {others.map((c) => <option key={c} value={c}>{label(c)}</option>)}
        </Select>{err('country')}</label>
      <label className="block"><span className="text-xs font-semibold">Payout method</span>
        <Select value={method} onChange={(e) => setMethod(e.target.value as PayoutMethod)} className="mt-1 w-full" {...described('method')}>
          {methods.map((m) => <option key={m} value={m}>{PAYOUT_METHOD_LABELS[m]}</option>)}
        </Select>{err('method')}</label>
      <label className="block"><span className="text-xs font-semibold">{method === 'amazon_gift_card' ? 'Your full name' : 'Account holder name'}</span>
        <Input value={holder} onChange={(e) => setHolder(e.target.value)} className="mt-1" autoComplete="name" {...described('holder')} />{err('holder')}</label>
      {fields.map((f) => (
        <label key={f.key} className="block"><span className="text-xs font-semibold">{f.label}{f.optional ? ' (optional)' : ''}</span>
          <Input value={details[f.key] ?? ''} placeholder={f.placeholder} onChange={(e) => setDetails({ ...details, [f.key]: e.target.value })} className="mt-1 font-mono" autoComplete="off" {...described(f.key)} />{err(f.key)}</label>
      ))}
      {method === 'amazon_gift_card' && <p className="text-xs text-muted-foreground">Gift cards are for Amazon.com (US store) and are emailed to you.</p>}
      {method === 'wise' && <p className="text-xs text-muted-foreground">Paid to your Wise account; Wise converts to your currency.</p>}
      <div className="flex items-center gap-3">
        <Button type="submit" disabled={state === 'saving'}>{state === 'saving' ? <><Loader2 className="h-4 w-4 animate-spin" />Saving…</> : 'Save payout details'}</Button>
        {state === 'saved' && <span className="text-xs font-semibold text-emerald-600">Saved.</span>}
        {state !== 'idle' && state !== 'saving' && state !== 'saved' && <span className="text-xs text-rose-600">{state}</span>}
      </div>
      <p className="text-xs text-muted-foreground">Your payout details are encrypted. The {BRAND.name} team sees a masked summary; every time they open the full details it is logged.</p>
    </form>
  );
}
